import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MapPin } from "lucide-react";
import { fetchLocationSuggestions, resolveZip, type LocationSuggestion } from "@/lib/location-autocomplete";

/**
 * City + State + (optional) ZIP inputs with autocomplete.
 * - Typing a 5-digit ZIP auto-fills city + state via api.zippopotam.us (US, free, no key).
 * - Typing a city queries Photon (OpenStreetMap, free, no key) and offers suggestions
 *   that fill city + state on selection.
 * Designed to be reusable across the app.
 */
export function CityZipInput({
  city,
  state,
  zip,
  onChange,
  required,
  showZip = true,
  labels,
  className,
}: {
  city: string;
  state: string;
  zip?: string;
  onChange: (v: { city: string; state: string; zip?: string }) => void;
  required?: boolean;
  showZip?: boolean;
  labels?: { city?: string; state?: string; zip?: string };
  className?: string;
}) {
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [zipSuggestions, setZipSuggestions] = useState<LocationSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [zipOpen, setZipOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const zipWrapRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!wrapRef.current?.contains(target)) setOpen(false);
      if (!zipWrapRef.current?.contains(target)) setZipOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // ZIP → city/state
  const handleZip = async (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 5);
    onChange({ city, state, zip: digits });
    if (digits.length !== 5) {
      setZipSuggestions([]);
      setZipOpen(false);
      return;
    }
    setZipLoading(true);
    try {
      const places = await resolveZip(digits);
      setZipSuggestions(places);
      setZipOpen(places.length > 0);
      if (places.length === 1) onChange({ city: places[0].city || city, state: places[0].state || state, zip: digits });
    } catch {}
    setZipLoading(false);
  };

  // City → suggestions via Photon
  const handleCity = (val: string) => {
    onChange({ city: val, state, zip });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const items = await fetchLocationSuggestions(val);
        setSuggestions(items);
        setOpen(items.length > 0);
      } catch {}
      setLoading(false);
    }, 300);
  };

  const pick = (s: LocationSuggestion) => {
    onChange({ city: s.city, state: s.state, zip: s.zip ?? zip });
    setOpen(false);
  };

  return (
    <div className={`grid gap-4 ${showZip ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 md:grid-cols-2"} ${className ?? ""}`}>
      <div className="relative" ref={wrapRef}>
        <Label className="text-xs">
          {labels?.city ?? "City"}{required ? " *" : ""}
        </Label>
        <div className="relative mt-1">
          <Input
            value={city}
            onChange={(e) => handleCity(e.target.value)}
            onFocus={() => suggestions.length && setOpen(true)}
            placeholder="Start typing a city…"
            autoComplete="off"
          />
          {loading && (
            <Loader2 className="h-3.5 w-3.5 animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          )}
        </div>
        {open && suggestions.length > 0 && (
          <ul className="absolute z-50 mt-1 w-full max-h-64 overflow-auto border border-border bg-popover shadow-md">
            {suggestions.map((s) => (
              <li key={s.label}>
                <button
                  type="button"
                  onClick={() => pick(s)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                >
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{s.label}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <Label className="text-xs">{labels?.state ?? "State"}</Label>
        <Input
          value={state}
          onChange={(e) => onChange({ city, state: e.target.value, zip })}
          placeholder="e.g. CA"
          className="mt-1"
          autoComplete="address-level1"
        />
      </div>

      {showZip && (
        <div className="relative" ref={zipWrapRef}>
          <Label className="text-xs">{labels?.zip ?? "ZIP code"}</Label>
          <div className="relative mt-1">
            <Input
              value={zip ?? ""}
              onChange={(e) => handleZip(e.target.value)}
              onFocus={() => zipSuggestions.length && setZipOpen(true)}
              placeholder="5-digit ZIP"
              inputMode="numeric"
              maxLength={5}
              autoComplete="postal-code"
            />
            {zipLoading && (
              <Loader2 className="h-3.5 w-3.5 animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            )}
          </div>
          {zipOpen && zipSuggestions.length > 0 && (
            <ul className="absolute z-50 mt-1 w-full max-h-64 overflow-auto border border-border bg-popover shadow-md">
              {zipSuggestions.map((s) => (
                <li key={s.label}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange({ city: s.city, state: s.state, zip: s.zip ?? zip });
                      setZipOpen(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                  >
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{s.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
